import { useCallback, useEffect, useState } from 'react'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { adminClient } from '../../api'
import type { UsageFee, UsageFeeExchange, UsageFeeQuoteAsset } from '../../api/statisticsTypes'
import { formatNumberByCoin } from '../trading/coinConfigLabels'
import { AdminNotice, EmailLookupForm } from './adminShared'
import { describeAdminError, useEmailParam } from './adminHelpers'
/**
 * Equivalent of legacy `AdminUserUsagefeeComponent` (route
 * `admin/users/usagefee`, contract #30 — same B3 `UsageFee` shape as the
 * user-facing statistics page, apexcharts on the legacy page per the audit).
 * Shows the exchange -> quote-asset summary; the coin/strategy breakdown
 * underneath each quote asset is available as raw JSON per exchange (round
 * 1 does not re-derive a 3-level nested table for an admin lookup tool —
 * nothing is dropped, it is one click away).
 */
export function AdminUserUsagefeePage() {
  const [email, setEmail] = useEmailParam()
  const [month, setMonth] = useState('')
  const [data, setData] = useState<UsageFee | null>(null)
  const [feedback, setFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()
  const [expanded, setExpanded] = useState<string | null>(null)

  const load = useCallback((targetEmail: string, targetMonth: string) => {
    setData(null)
    setFeedback(undefined)
    adminClient
      .usageFeeMonth(targetEmail, targetMonth || undefined)
      .then((result) => setData(result))
      .catch((err) => setFeedback(describeAdminError(err)))
  }, [])

  useEffect(() => {
    if (email) load(email, month)
    else setData(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  const quoteAssetColumns: Column<UsageFeeQuoteAsset>[] = [
    { key: 'coin2', label: 'Quote asset', align: 'left', sortValue: (r) => r.coin2 },
    { key: 'count', label: 'Count', align: 'right', numeric: true, sortValue: (r) => r.count },
    { key: 'amount', label: 'Amount', align: 'right', numeric: true, sortValue: (r) => r.amount, format: (_v, r) => formatNumberByCoin(r.amount, r.coin2) },
    { key: 'systemFee', label: 'System fee', align: 'right', numeric: true, sortValue: (r) => r.systemFee, format: (_v, r) => formatNumberByCoin(r.systemFee, r.coin2) },
    { key: 'strategyCount', label: 'Strategy count', align: 'right', numeric: true, sortValue: (r) => r.strategyCount },
    { key: 'strategyFee', label: 'Strategy fee', align: 'right', numeric: true, sortValue: (r) => r.strategyFee, format: (_v, r) => formatNumberByCoin(r.strategyFee, r.coin2) },
  ]

  function renderExchange(ex: UsageFeeExchange) {
    return (
      <div className="portal-card" key={ex.exchangeId}>
        <h3>{ex.exchangeId}</h3>
        {ex.items.length === 0 ? (
          <p className="portal-text-muted">No usage fees this period.</p>
        ) : (
          <div className="portal-table-scroll">
            <Table columns={quoteAssetColumns} rows={ex.items} rowKey={(r) => r.coin2} />
          </div>
        )}
        <button type="button" onClick={() => setExpanded((cur) => (cur === ex.exchangeId ? null : ex.exchangeId))}>
          {expanded === ex.exchangeId ? 'hide coin/strategy breakdown' : 'show coin/strategy breakdown'}
        </button>
        {expanded === ex.exchangeId && <pre>{JSON.stringify(ex.items, null, 2)}</pre>}
      </div>
    )
  }

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — user usage fee</h1>
      </div>
      <div className="portal-card">
        <EmailLookupForm email={email} onSubmit={setEmail} />
        <form
          className="portal-toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            if (email) load(email, month)
          }}
        >
          <label htmlFor="usagefee-month">Month (YYYYMM, blank = this month)</label>
          <input id="usagefee-month" value={month} onChange={(e) => setMonth(e.target.value)} placeholder="202609" />
          <button type="submit" disabled={!email}>
            Reload
          </button>
        </form>
        <AdminNotice feedback={feedback} />
        {!email && <p className="portal-text-muted">Enter a user e-mail to view their usage fees.</p>}
        {email && data === null && !feedback && <p>Loading…</p>}
      </div>
      {email && data !== null && (data.exchanges.length === 0 ? <p className="portal-text-muted">No usage fees.</p> : data.exchanges.map(renderExchange))}
    </div>
  )
}
